/**
 * CelebrationQueue — shows a milestone unlock overlay, one at a time,
 * for 4s (visible duration) + a 600ms fade-out. Back-to-back unlocks
 * queue and play in order so they never visually stomp.
 *
 * Spec: signage-device-app-spec §3.2 — 1.2s scale-in + ~4s hold + fade.
 * Animation is done via CSS keyframes in styles.css; JS owns only the
 * queue + the fixed-position overlay node. prefers-reduced-motion is
 * handled in CSS.
 */

const VISIBLE_MS = 4000;
const EXIT_MS = 600;

export class CelebrationQueue {
  private readonly queue: CelebrationItem[] = [];
  private active: HTMLElement | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly root: HTMLElement) {}

  enqueueProgress(item: ProgressCelebrationItem): void {
    this.queue.push({ kind: 'progress', ...item });
    this.promote();
  }

  /**
   * After a full root re-render, the active overlay node (which lives
   * outside the panel tree for z-index reasons) will have been wiped.
   * Re-attach it so the in-flight animation survives the re-render.
   */
  reattach(): void {
    if (this.active && !this.active.isConnected) {
      this.root.appendChild(this.active);
    }
  }

  dispose(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.active?.remove();
    this.active = null;
    this.queue.length = 0;
  }

  private promote(): void {
    if (this.active !== null || this.queue.length === 0) return;
    const next = this.queue.shift()!;
    this.active = this.build(next);
    this.root.appendChild(this.active);

    this.timer = setTimeout(() => this.beginExit(), VISIBLE_MS);
  }

  private beginExit(): void {
    if (!this.active) return;
    this.active.classList.add('ag-celebration--exit');
    this.timer = setTimeout(() => {
      this.active?.remove();
      this.active = null;
      this.timer = null;
      this.promote();
    }, EXIT_MS);
  }

  private build(item: CelebrationItem): HTMLElement {
    const overlay = document.createElement('div');
    overlay.className = `ag-celebration ag-celebration--${item.kind}`;
    overlay.style.setProperty('--ag-primary', item.accent);
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-live', 'polite');

    const confetti = document.createElement('div');
    confetti.className = 'ag-celebration__confetti';
    confetti.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < 24; i += 1) {
      const piece = document.createElement('span');
      piece.style.setProperty('--piece-index', String(i));
      piece.style.setProperty('--piece-left', `${(i * 37) % 100}%`);
      confetti.appendChild(piece);
    }
    overlay.appendChild(confetti);

    const card = document.createElement('div');
    card.className = 'ag-celebration__card';
    overlay.appendChild(card);

    const badge = document.createElement('div');
    badge.className = 'ag-celebration__badge';
    badge.textContent = `${item.percent}%`;
    card.appendChild(badge);

    const kicker = document.createElement('div');
    kicker.className = 'ag-celebration__kicker';
    kicker.textContent = item.kicker ?? (item.percent === 100 ? 'Goal reached' : 'Progress milestone');
    card.appendChild(kicker);

    const h = document.createElement('div');
    h.className = 'ag-celebration__title';
    h.textContent = item.title;
    card.appendChild(h);

    const session = document.createElement('div');
    session.className = 'ag-celebration__session';
    session.textContent = item.sessionName;
    card.insertBefore(session, kicker);

    const amounts = document.createElement('div');
    amounts.className = 'ag-celebration__amounts';
    amounts.textContent = `${item.amount} of ${item.goal}`;
    card.appendChild(amounts);

    const track = document.createElement('div');
    track.className = 'ag-celebration__progress';
    const fill = document.createElement('span');
    fill.style.width = `${Math.max(0, Math.min(100, item.percent))}%`;
    track.appendChild(fill);
    card.appendChild(track);

    return overlay;
  }
}

export interface ProgressCelebrationItem {
  percent: number;
  title: string;
  kicker?: string;
  sessionName: string;
  amount: string;
  goal: string;
  accent: string;
}

type CelebrationItem = { kind: 'progress' } & ProgressCelebrationItem;
